// ============================================================================
// TENANT USER ROLE SERVICE
// ============================================================================
// Assigns and revokes roles for a user within one tenant.
//
// Every endpoint here needs tenant context. Enterprise admin tokens carry no
// tenant claim, so each call sends the tenant explicitly via the X-Tenant-Id
// header — without it the API answers "Tenant context is required".

import { get, post, del } from '../client/axiosClient';
import { tenantUserRoleEndpoints } from '../endpoints/tenantUserRoles.endpoints';
import type { ID, TenantUserRoleResponse, AssignTenantRoleRequest } from '../types';
import type { TenantHubApiResponse } from '../types/auth.types';

/** Builds the per-request tenant context header. */
const tenantHeader = (tenantId: ID) => ({ headers: { 'X-Tenant-Id': String(tenantId) } });

class TenantUserRoleService {
  /**
   * Roles assigned to a user within a tenant.
   * GET /TenantUserRole/user/:userId
   * @param userId GLOBAL user id (not the tenant-user row id)
   */
  async getUserRoles(userId: ID, tenantId: ID): Promise<TenantHubApiResponse<TenantUserRoleResponse[]>> {
    const response = await get<TenantHubApiResponse<TenantUserRoleResponse[]>>(
      tenantUserRoleEndpoints.getUserRoles(userId),
      tenantHeader(tenantId),
    );
    return response.data;
  }

  /**
   * Users holding a given role in a tenant.
   * GET /TenantUserRole/role/:roleId
   */
  async getUsersWithRole(roleId: ID, tenantId: ID): Promise<TenantHubApiResponse<TenantUserRoleResponse[]>> {
    const response = await get<TenantHubApiResponse<TenantUserRoleResponse[]>>(
      tenantUserRoleEndpoints.getUsersWithRole(roleId),
      tenantHeader(tenantId),
    );
    return response.data;
  }

  /**
   * Whether a user already holds a role.
   * GET /TenantUserRole/check
   */
  async check(userId: ID, roleId: ID, tenantId: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await get<TenantHubApiResponse<boolean>>(
      tenantUserRoleEndpoints.check(userId, roleId),
      tenantHeader(tenantId),
    );
    return response.data;
  }

  /**
   * Assign a role to a user. The API also mirrors the assignment into Keycloak
   * when the role exists there; when it does not, the database assignment still
   * succeeds and the response message reports the Keycloak outcome.
   * POST /TenantUserRole/assign
   */
  async assign(data: AssignTenantRoleRequest): Promise<TenantHubApiResponse<TenantUserRoleResponse>> {
    const response = await post<TenantHubApiResponse<TenantUserRoleResponse>>(
      tenantUserRoleEndpoints.assign(),
      data,
      tenantHeader(data.tenantId),
    );
    return response.data;
  }

  /**
   * Revoke an assignment by its id.
   * DELETE /TenantUserRole/:id
   */
  async revoke(assignmentId: ID, tenantId: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await del<TenantHubApiResponse<boolean>>(
      tenantUserRoleEndpoints.revoke(assignmentId),
      tenantHeader(tenantId),
    );
    return response.data;
  }

  /**
   * Revoke by user + role rather than assignment id.
   * DELETE /TenantUserRole/revoke
   */
  async revokeByDetails(userId: ID, roleId: ID, tenantId: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await del<TenantHubApiResponse<boolean>>(
      tenantUserRoleEndpoints.revokeByDetails(userId, roleId),
      tenantHeader(tenantId),
    );
    return response.data;
  }
}

export const tenantUserRoleService = new TenantUserRoleService();
export default tenantUserRoleService;
